
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FileText, AlertCircle, CheckCircle } from "lucide-react";

interface KnowledgeSummaryData {
  title: string;
  category: string;
  content: string;
  description?: string;
}

interface KnowledgeSummaryFormProps {
  onSubmit: (data: KnowledgeSummaryData) => void;
  isSubmitting: boolean;
  onCancel: () => void;
}

export const KnowledgeSummaryForm: React.FC<KnowledgeSummaryFormProps> = ({
  onSubmit,
  isSubmitting,
  onCancel
}) => {
  const [formData, setFormData] = useState<KnowledgeSummaryData>({
    title: '',
    category: 'nav-rapporter',
    content: '',
    description: ''
  });
  
  const [error, setError] = useState<string>('');

  const maxCharacters = 30000; // ~10 pages of text
  const minCharacters = 100;
  
  const handleContentChange = (value: string) => {
    if (value.length <= maxCharacters) {
      setFormData(prev => ({ ...prev, content: value }));
      setError('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Tittel er påkrevd');
      return;
    }
    
    if (formData.content.length < minCharacters) {
      setError(`Innhold må være minst ${minCharacters} tegn`);
      return;
    }
    
    if (formData.content.length > maxCharacters) {
      setError(`Innhold kan ikke overstige ${maxCharacters} tegn`);
      return;
    }
    
    onSubmit(formData);
  };

  const getCharacterStatus = () => {
    const length = formData.content.length;
    if (length < minCharacters) return { color: 'text-orange-600', icon: AlertCircle };
    if (length > maxCharacters * 0.9) return { color: 'text-orange-600', icon: AlertCircle };
    return { color: 'text-green-600', icon: CheckCircle };
  };

  const characterStatus = getCharacterStatus();
  const StatusIcon = characterStatus.icon;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="title">Tittel</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="F.eks. NAVs omverdenanalyse 2025 - Sammendrag"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="category">Kategori</Label>
        <Select 
          value={formData.category} 
          onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Velg kategori" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="nav-rapporter">NAV Rapporter</SelectItem>
            <SelectItem value="arbeidsmarked">Arbeidsmarked</SelectItem>
            <SelectItem value="utdanning">Utdanning</SelectItem>
            <SelectItem value="karriere">Karriere</SelectItem>
            <SelectItem value="kompetanse">Kompetanse</SelectItem>
            <SelectItem value="general">General</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Kort beskrivelse (Valgfritt)</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Kort beskrivelse av innholdet"
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="content">Sammendrag / Innhold</Label>
          <div className={`flex items-center gap-1 text-sm ${characterStatus.color}`}>
            <StatusIcon className="h-4 w-4" />
            <span>{formData.content.length.toLocaleString()} / {maxCharacters.toLocaleString()}</span>
          </div>
        </div>
        <Textarea
          id="content"
          value={formData.content}
          onChange={(e) => handleContentChange(e.target.value)}
          placeholder="Lim inn eller skriv sammendrag av dokumentet her. Du kan inkludere opptil ~10 sider med tekst (30,000 tegn). 

Eksempel:
NAVs omverdenanalyse 2025 identifiserer teknologisk omstilling, det grønne skiftet og aldrende befolkning som hovedtrender som vil påvirke arbeidsmarkedet fram mot 2035...

Tips:
- Fokuser på de viktigste innsiktene
- Inkluder konkrete tall og prognoser
- Beskriv implikasjoner for arbeidsmarkedet
- Nevn spesifikke bransjer og yrkesgrupper"
          rows={20}
          className="font-mono text-sm"
        />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Alert>
        <FileText className="h-4 w-4" />
        <AlertDescription>
          Dette sammendraget vil bli behandlet som ett enkelt dokument og indeksert for semantisk søk. 
          Kvaliteten på sammendraget påvirker hvor godt Klara kan finne og bruke informasjonen.
        </AlertDescription>
      </Alert>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Avbryt
        </Button>
        <Button type="submit" disabled={isSubmitting || formData.content.length < minCharacters}>
          {isSubmitting ? 'Lagrer...' : 'Lagre sammendrag'}
        </Button>
      </div>
    </form>
  );
};
